import { sectionForPath } from "@/config/routeOwnership";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/scrollToSection";
import { Menu, ChevronDown, MessageSquare, Accessibility, UserCircle, Shield, FileCode, Wrench, DollarSign, CalendarCheck, Sparkles, ImageIcon, Activity, MessageSquareText, Trophy, GraduationCap, Users, Gamepad2, UserCog, BookOpen, Video, Github, Film, Brain, LogOut, PlayCircle, LogIn, Clapperboard } from "lucide-react";
import { Suspense, lazy, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import geniebotRobotLogo from "@/assets/geniebot-robot-logo.webp";
import { LEARN_LINKS } from "@/config/learnNav";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/contexts/AdminRoleContext";
import { HeaderInfoBar } from "@/components/header-info";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AccessibilityQuickToggles } from "@/components/AccessibilityQuickToggles";
import { GenieBotPulse } from "@/components/GenieBotPulse";
import { NetworkStatusGroup } from "@/components/NetworkStatusGroup";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { UserMenu } from "@/components/user/UserMenu";
import { GamificationNavBadge } from "@/components/gamification/GamificationNavBadge";
import { GenieBotIcon } from "@/components/ui/GenieBotIcon";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { WhatsNewBadge } from "@/components/navigation/WhatsNewBadge";
import { useWhatsNew } from "@/hooks/useWhatsNew";
import { KeyboardShortcutsSheet } from "@/components/KeyboardShortcutsSheet";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { T } from "@/components/T";

const WelcomeModal = lazy(() => import("@/components/WelcomeModal").then(m => ({ default: m.WelcomeModal })));

type NavItem = {
  name: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  submenu?: NavItem[];
};

// Streamlined: 5 top-level items (Dashboard, Chat, Tools, Settings, About)
// Duplicates removed — Games, Everyday Suite, College Ready live under Tools only
const pages: NavItem[] = [
  { name: "Dashboard", href: "/dashboard" },
  { name: "Chat", href: "/chat" },
  { 
    name: "Tools",
    submenu: [
      { name: "All Tools", href: "/tools", icon: Wrench },
      { name: "Creative Director", href: "/tools/creative-director", icon: Clapperboard },
      { name: "Storyboard Video Studio", href: "/tools/storyboard-video-studio", icon: Clapperboard },
      { name: "Character Replacement", href: "/tools/character-replacement", icon: UserCog },
      { name: "Games Arcade", href: "/games", icon: Gamepad2 },
      { name: "Pic Editor", href: "/pic-editor", icon: ImageIcon },
      
      { name: "Open Video Studio", href: "/tools/open-video", icon: Film },
      { name: "Avatar Studio", href: "/tools/avatar-studio", icon: Film },
      // Prompt tools (creation & management)
      { name: "Prompt Studio", href: "/prompt-studio", icon: Sparkles },
      { name: "Prompt Conversationist", href: "/prompt-conversationist", icon: MessageSquare },
      { name: "Prompt Template Factory", href: "/prompt-template-factory", icon: Sparkles },
      { name: "My Prompts", href: "/prompts/my", icon: BookOpen },
      { name: "Public Prompts", href: "/prompts/public", icon: Users },
    ]
  },
  {
    name: "Learn",
    submenu: LEARN_LINKS.map(({ name, href, icon }) => ({ name, href, icon })),
  },
  { 
    name: "More",
    submenu: [
      
      { name: "Profile Settings", href: "/profile-settings", icon: UserCircle },
      { name: "Analytics", href: "/analytics", icon: Activity },
      { name: "Accessibility", href: "/accessibility-settings", icon: Accessibility },
      { name: "What's New", href: "/changelog", icon: Sparkles },
      { name: "About", href: "/about" },
      { name: "Help & FAQ", href: "/help" },
      { name: "All pages (site map)", href: "/site-map" },
    ]
  },
];

export default function NavBar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const navigate = useNavigate();

  const handleHireAIKennyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (location.pathname === "/") {
      void scrollToSection("hire-ai-kenny");
    } else {
      navigate("/#hire-ai-kenny");
    }
  };
  const { user } = useAuth();
  const { isAdmin } = useAdminRole();
  const { pendingCount } = useOfflineQueue();
  const { hasNewItems: hasNewChangelog } = useWhatsNew();

  const handleLogoClick = () => {
    navigate(user ? '/dashboard' : '/');
  };

  const handleSignOut = async () => {
    setOpen(false);
    try {
      await supabase.auth.signOut();
    } finally {
      navigate('/auth', { replace: true });
    }
  };

  const handleWatchIntro = () => {
    setOpen(false);
    setShowIntro(true);
  };

  const navPages: NavItem[] = isAdmin 
    ? [...pages, { 
        name: "Admin", 
        submenu: [
          { name: "Training Assets", href: "/admin/training-assets", icon: Shield },
          { name: "Bookings", href: "/admin/bookings", icon: CalendarCheck },
          { name: "Security Audit", href: "/admin/audit", icon: Activity },
          { name: "User Feedback", href: "/admin/feedback", icon: MessageSquareText },
          { name: "Affiliates", href: "/admin/affiliates", icon: Users },
          { name: "User Roles", href: "/admin/user-roles", icon: UserCog },
        ]
      }]
    : pages;

  return (
    <>
      <HeaderInfoBar />
      <header className="sticky top-0 z-40 md:backdrop-blur-lg bg-background md:bg-background/80 border-b border-border shadow-lg animate-slide-in-down">
        <div className="mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center gap-4">
          <button onClick={handleLogoClick} aria-label="GenieBot Chat — go to home page" className="flex items-center gap-2 gb-link group cursor-pointer">
            <div className="relative shrink-0 h-7 w-7 md:h-8 md:w-8">
              <img 
                src={geniebotRobotLogo} 
                alt="" 
                className="h-full w-full rounded-full object-cover border-2 border-primary group-hover:scale-110 transition-all duration-300"
                width={32}
                height={32}
              />
              {/* Status pulse pinned inside the logo's top-right corner so it
                  never bleeds over the "GenieBot Chat" wordmark. */}
              <div className="pointer-events-none absolute -top-0.5 -right-0.5 origin-top-right scale-[0.45]">
                <GenieBotPulse size="sm" />
              </div>
            </div>

            <span className="font-semibold text-sm md:text-base">
              <span className="text-foreground group-hover:text-primary transition-colors duration-300">GenieBot</span>{" "}
              <span className="text-primary hidden sm:inline">Chat</span>
            </span>
            <span className="hidden sm:inline ml-2 bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 text-foreground text-xs px-2 py-0.5 rounded-full shadow-sm group-hover:scale-110 transition-transform duration-300">
              Free
            </span>
          </button>

        <nav className="ml-auto hidden lg:flex items-center gap-4 xl:gap-6">
          {navPages.map((p) => (
            p.submenu ? (
              <DropdownMenu key={p.name}>
                <DropdownMenuTrigger
                  aria-current={(sectionForPath(location.pathname) === p.name || p.submenu.some((i) => location.pathname === i.href || location.pathname.startsWith(i.href + "/"))) ? "page" : undefined}
                  className={`focus-ring relative pb-1 flex items-center gap-1 rounded-sm transition-colors text-sm ${
                    (sectionForPath(location.pathname) === p.name || p.submenu.some((i) => location.pathname === i.href || location.pathname.startsWith(i.href + "/")))
                      ? "text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {p.name}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {p.submenu.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link 
                          to={item.href}
                          className={`w-full cursor-pointer flex items-center gap-2 ${
                            location.pathname === item.href ? "bg-primary/10 text-foreground font-medium" : ""
                          }`}
                        >
                          {ItemIcon && <ItemIcon className="h-4 w-4" />}
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                key={p.name}
                to={p.href!}
                onClick={(e) => {
                  if (p.href === "/chat" && location.pathname === "/chat") {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent("geniebot:new-chat"));
                    navigate("/chat", { state: { newChatNonce: Date.now() } });
                  }
                }}
                aria-current={location.pathname === p.href || sectionForPath(location.pathname) === p.name ? "page" : undefined}
                className={`focus-ring rounded-sm transition-colors relative pb-1 text-sm flex items-center gap-1.5 ${
                  location.pathname === p.href || sectionForPath(location.pathname) === p.name
                    ? "text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {p.icon && <p.icon className="h-4 w-4" />}
                {p.name}
              </Link>
            )
          ))}
          <a
            href="/#hire-ai-kenny"
            onClick={handleHireAIKennyClick}
            className="hidden lg:inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <CalendarCheck className="h-4 w-4" />
            Hire AI Kenny
          </a>
          <NotificationCenter />
          <GamificationNavBadge />
          <div className="hidden xl:flex items-center gap-4">
            <NetworkStatusGroup pendingCount={pendingCount} />
            <AccessibilityQuickToggles />
            <KeyboardShortcutsSheet />
          </div>
          <LanguageSwitcher />
          <ThemeToggle />
          {user ? (
            <UserMenu />
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/auth')}
              className="gap-1.5"
            >
              <LogIn className="h-4 w-4" />
              Sign in
            </Button>
          )}
        </nav>

        {/* Mobile auth quick access */}
        <div className="flex items-center gap-2 ml-auto lg:hidden">
          {user ? (
            <UserMenu />
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/auth')}
              className="gap-1.5 h-9"
            >
              <LogIn className="h-4 w-4" />
              Sign in
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[280px] sm:w-[320px] bg-card border-border overflow-hidden">
            <nav className="flex flex-col gap-4 mt-8 overflow-y-auto max-h-[calc(100vh-80px)] pb-8">
              {navPages.map((p) => (
                p.submenu ? (
                  <div key={p.name} className="flex flex-col gap-2">
                    <div className="px-4 py-2 text-sm font-medium text-muted-foreground">
                      {p.name}
                    </div>
                    {p.submenu.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setOpen(false)}
                          className={`px-4 py-3 ml-2 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                            location.pathname === item.href
                              ? "bg-primary/10 text-primary border-l-4 border-primary font-medium shadow-lg"
                              : "text-foreground hover:bg-muted border-l-4 border-transparent hover:border-primary/30"
                          }`}
                        >
                          {ItemIcon && <ItemIcon className="h-4 w-4" />}
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <Link
                    key={p.name}
                    to={p.href!}
                    onClick={() => setOpen(false)}
                    className={`px-4 py-3 rounded-lg transition-all duration-300 stagger-item ${
                      location.pathname === p.href
                        ? "bg-primary/10 text-primary border-l-4 border-primary font-medium shadow-lg"
                        : "text-foreground hover:bg-muted border-l-4 border-transparent hover:border-primary/30"
                    }`}
                  >
                    {p.name}
                  </Link>
                )
              ))}

              <div className="border-t border-border mt-4 pt-4 flex flex-col gap-1">
                <button
                  onClick={handleWatchIntro}
                  className="mx-2 px-4 py-3 rounded-lg flex items-center gap-2 text-foreground hover:bg-muted transition-colors text-left"
                >
                  <PlayCircle className="h-4 w-4" />
                  Watch intro video
                </button>

                {user ? (
                  <>
                    <div className="px-4 py-2 text-xs text-muted-foreground truncate">
                      Signed in as <span className="text-foreground">{user.email}</span>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="mx-2 px-4 py-3 rounded-lg flex items-center gap-2 text-destructive hover:bg-destructive/10 transition-colors text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setOpen(false)}
                    className="mx-2 px-4 py-3 rounded-lg flex items-center gap-2 text-primary hover:bg-primary/10 transition-colors"
                  >
                    <LogIn className="h-4 w-4" />
                    Sign in
                  </Link>
              )}

              <a
                href="/#hire-ai-kenny"
                onClick={(e) => {
                  setOpen(false);
                  handleHireAIKennyClick(e);
                }}
                className="mx-2 px-4 py-3 rounded-lg flex items-center gap-2 text-primary hover:bg-primary/10 transition-colors"
              >
                <CalendarCheck className="h-4 w-4" />
                Hire AI Kenny
              </a>

                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Theme</span>
                  <ThemeToggle />
                </div>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
        </div>
      </header>

      {showIntro && (
        <Suspense fallback={null}>
          <WelcomeModal open={showIntro} onOpenChange={setShowIntro} autoPlay={true} />
        </Suspense>
      )}
    </>
  );
}
